import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getActiveLeads } from '../lib/supabase';
import { isDmeOnly } from '../lib/serviceModel';

const ZONES = [
  { key:'Outreach',        label:'Outreach',         color:'var(--zone-marketing)',  bg:'var(--zone-marketing-bg)',  statuses:['New Referral','Client Contacted','Response Pending','Waitlist'] },
  { key:'Clinical Intake', label:'Clinical Intake',  color:'var(--zone-processing)', bg:'var(--zone-processing-bg)', statuses:['Intake Form Submitted','Nursing Assessment Pending','Nursing Assessment Completed','PPOT Request Pending','PPOT Request In Progress'] },
  { key:'Authorization',   label:'Authorization',    color:'var(--zone-approval)',   bg:'var(--zone-approval-bg)',   statuses:['Submitted (DMA6A)','Submitted (DMA80)'] },
  { key:'Active',          label:'Active / In Service', color:'var(--zone-active)',  bg:'var(--zone-active-bg)',     statuses:['Approved by GAPP','Active (Receiving Service)'] },
  { key:'Inactive',        label:'Inactive',          color:'var(--zone-closed)',    bg:'var(--zone-closed-bg)',     statuses:['On-Hold/Suspended','Service Suspended','Discharged','GAPP Application Denied','Duplicate','Lost'] },
];

const LEAD_STATUSES_DISPLAY = [
  { key: 'New Lead', color: 'var(--zone-marketing)', bg: 'var(--zone-marketing-bg)' },
  { key: 'Contacted', color: 'var(--zone-processing)', bg: 'var(--zone-processing-bg)' },
  { key: 'Interested', color: 'var(--zone-approval)', bg: 'var(--zone-approval-bg)' },
  { key: 'Follow Up', color: 'var(--zone-active)', bg: 'var(--zone-active-bg)' },
];

export default function Pipeline() {
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      getClients().then(({ data }) => setClients(data || [])),
      getActiveLeads().then(({ data }) => setLeads(data || [])),
    ]).then(() => setLoading(false));
  }, []);

  const clientsByZone = (zone) => clients.filter(c => c.client_status_zone === zone.key);

  const leadsColor = 'var(--zone-leads)';
  const leadsBg = 'var(--zone-leads-bg)';

  return (
    <div>
      <div className="pipeline-board" style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:8 }}>

        {/* Leads column — leftmost */}
        <div className="pipeline-col" style={{ background: 'var(--topbar-bg)', border:'0.5px solid var(--border)', borderRadius:12, minWidth:240, flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'12px 14px 10px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.09em', color:leadsColor }}>Leads</span>
              <span style={{ fontSize:9, fontWeight:700, background:leadsColor, color:'#fff', padding:'1px 5px', borderRadius:3 }}>NEW</span>
            </div>
            <span style={{ fontSize:11, fontWeight:700, background:leadsColor, color:'#fff', width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>{leads.length}</span>
          </div>
          <div style={{ height:2, background:leadsColor, opacity:0.25, margin:'0 10px' }} />

          <div style={{ padding:10, flex:1, overflowY:'auto', maxHeight:'calc(100vh - 220px)', display:'flex', flexDirection:'column', gap:8 }}>
            {loading && <div style={{ textAlign:'center', padding:20 }}><div className="loading-spinner" style={{ margin:'0 auto' }} /></div>}
            {!loading && leads.length === 0 && (
              <div style={{ textAlign:'center', padding:20, color:leadsColor, fontSize:12, opacity:0.6 }}>No active leads</div>
            )}
            {leads.map(lead => {
              const statusInfo = LEAD_STATUSES_DISPLAY.find(s => s.key === lead.current_status) || LEAD_STATUSES_DISPLAY[0];
              return (
                <div
                  key={lead.id}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  style={{ background:'var(--pipeline-card-bg)', border:'1px solid var(--border)', borderRadius:9, padding:'10px 12px', cursor:'pointer', transition:'all 0.15s', boxShadow:'var(--shadow-pipeline-card)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = leadsColor; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', marginBottom:2 }}>{lead.last_name}, {lead.first_name}</div>
                  <div style={{ fontSize:10, color:leadsColor, fontWeight:500, marginBottom:6 }}>{lead.lead_id}{lead.county ? ` · ${lead.county}` : ''}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:10, fontWeight:500, background: statusInfo.bg, color: statusInfo.color, padding:'2px 8px', borderRadius:20 }}>{lead.current_status}</span>
                  </div>
                  {lead.last_activity_note && (
                    <div style={{ fontSize:10, color:'var(--text2)', borderTop:'1px solid var(--border)', paddingTop:5, marginTop:6 }}>
                      {lead.last_activity_note.slice(0,60)}{lead.last_activity_note.length > 60 ? '...' : ''}
                    </div>
                  )}
                </div>
              );
            })}
            <button
              onClick={() => navigate('/leads')}
              style={{ marginTop:4, fontSize:11, color:leadsColor, background:'transparent', border:`1px dashed ${leadsColor}`, borderRadius:8, padding:'7px', cursor:'pointer', opacity:0.7 }}
            >
              + Add Lead
            </button>
          </div>
        </div>

        {/* Patient pipeline zones */}
        {ZONES.map(zone => {
          const zoneClients = clientsByZone(zone);
          return (
            <div key={zone.key} className="pipeline-col" style={{ background:'var(--topbar-bg)', border:'0.5px solid var(--border)', borderRadius:12, minWidth:240, flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'12px 14px 10px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.09em', color:zone.color }}>{zone.label}</span>
                <span style={{ fontSize:11, fontWeight:700, background:zone.color, color:'#fff', width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>{zoneClients.length}</span>
              </div>
              <div style={{ height:2, background:zone.color, opacity:0.25, margin:'0 10px' }} />

              <div style={{ padding:10, flex:1, overflowY:'auto', maxHeight:'calc(100vh - 220px)', display:'flex', flexDirection:'column', gap:8 }}>
                {loading && <div style={{ textAlign:'center', padding:20 }}><div className="loading-spinner" style={{ margin:'0 auto' }} /></div>}
                {!loading && zoneClients.length === 0 && (
                  <div style={{ textAlign:'center', padding:20, color:zone.color, fontSize:12, opacity:0.6 }}>No clients</div>
                )}
                {zoneClients.map(client => (
                  <div
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    style={{ background:'var(--pipeline-card-bg)', border:`1px solid var(--border)`, borderRadius:9, padding:'10px 12px', cursor:'pointer', transition:'all 0.15s', boxShadow:'var(--shadow-pipeline-card)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = zone.color; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', marginBottom:2 }}>{client.last_name}, {client.first_name}</div>
                    <div style={{ fontSize:10, color:zone.color, fontWeight:500, marginBottom:6 }}>{client.client_id} · {client.county}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      <span style={{ fontSize:10, fontWeight:500, background:zone.bg, color:zone.color, padding:'2px 8px', borderRadius:20, border:`1px solid ${zone.border}` }}>{client.current_status}</span>
                      <span style={{ fontSize:12, color: client.medicaid_eligible === 'Yes' ? 'var(--success)' : client.medicaid_eligible === 'No' ? 'var(--danger)' : 'var(--warn)' }} title={`Eligibility: ${client.medicaid_eligible || 'Pending'}`}>●</span>
                    </div>
                    {client.last_activity_note && (
                      <div style={{ fontSize:10, color:'var(--text2)', borderTop:'1px solid var(--border)', paddingTop:5, marginTop:6 }}>
                        {client.last_activity_note.slice(0,60)}{client.last_activity_note.length > 60 ? '...' : ''}
                      </div>
                    )}
                    {!client.nurse_assessment_complete && !isDmeOnly(client) && (
                      <div style={{ fontSize:10, color:'var(--warn)', marginTop:4 }}>⚠ Assessment pending</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
